
import { useState, FormEvent } from 'react';
import {
  upcomingAppointments,
  pastAppointments,
  availableTimeSlots,
  appointmentTypes,
} from '../../../mocks/clientPortalData';

const FORM_URL = 'https://readdy.ai/api/form/d63nsipov5qgbon321eg';

export default function AppointmentsSection() {
  const [showBooking, setShowBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', clientName);
      formBody.append('email', clientEmail);
      formBody.append('appointment_type', selectedType);
      formBody.append('date', selectedDate);
      formBody.append('time', selectedTime);
      formBody.append('notes', notes);

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setClientName('');
        setClientEmail('');
        setSelectedType('');
        setSelectedDate('');
        setSelectedTime('');
        setNotes('');
        setTimeout(() => {
          setShowBooking(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
      day: d.getDate(),
      month: d.toLocaleDateString('en-US', { month: 'short' }),
      weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
    };
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <section>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2
            className="text-2xl font-bold text-[#0a1628]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Appointments
          </h2>
          <p className="text-sm text-[#5a6a7e] mt-1">
            Manage and schedule your consultations
          </p>
        </div>
        <button
          onClick={() => {
            setShowBooking(!showBooking);
            setSubmitStatus('idle');
          }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#00e5c7] text-[#0a1628] rounded-md text-sm uppercase tracking-widest font-bold hover:bg-[#00ccb0] transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-[#00e5c7]/20"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${showBooking ? 'ri-close-line' : 'ri-add-line'} text-sm`}></i>
          </div>
          {showBooking ? 'Cancel' : 'Book Appointment'}
        </button>
      </div>

      {/* Booking Form */}
      {showBooking && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#00e5c7]/5 to-transparent"></div>
          <h3
            className="text-lg font-bold text-[#0a1628] mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Schedule a New Appointment
          </h3>

          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-emerald-500/10 rounded-full">
                <i className="ri-check-line text-3xl text-emerald-500"></i>
              </div>
              <h4
                className="text-xl font-bold text-[#0a1628] mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Appointment Requested!
              </h4>
              <p className="text-sm text-[#5a6a7e]">
                We&apos;ll confirm your appointment via email shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="appointment-booking-form"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    required
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] placeholder-[#5a6a7e]/50 focus:outline-none focus:border-[#00e5c7] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] placeholder-[#5a6a7e]/50 focus:outline-none focus:border-[#00e5c7] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Appointment Type
                  </label>
                  <select
                    name="appointment_type"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] focus:outline-none focus:border-[#00e5c7] transition-colors cursor-pointer appearance-none"
                  >
                    <option value="">Select type...</option>
                    {appointmentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={getMinDate()}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] focus:outline-none focus:border-[#00e5c7] transition-colors cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Preferred Time
                  </label>
                  <select
                    name="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] focus:outline-none focus:border-[#00e5c7] transition-colors cursor-pointer appearance-none"
                  >
                    <option value="">Select time...</option>
                    {availableTimeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={notes}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setNotes(e.target.value);
                      }
                    }}
                    maxLength={500}
                    placeholder="Any details about your appointment..."
                    rows={1}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#0a1628] placeholder-[#5a6a7e]/50 focus:outline-none focus:border-[#00e5c7] transition-colors resize-none"
                  />
                  <p className="text-[10px] text-[#5a6a7e] mt-1 text-right">{notes.length}/500</p>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-[#00e5c7] text-[#0a1628] rounded-md text-sm uppercase tracking-widest font-bold hover:bg-[#00ccb0] transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-[#00e5c7]/20 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Appointment'}
                </button>
                {submitStatus === 'error' && (
                  <p className="text-xs text-red-500">
                    Something went wrong. Please try again.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      )}

      {/* Upcoming Appointments */}
      <div className="mb-8">
        <h3 className="text-sm uppercase tracking-wider text-[#5a6a7e] font-semibold mb-4">
          Upcoming
        </h3>
        <div className="space-y-3">
          {upcomingAppointments.map((apt) => {
            const d = formatDate(apt.date);
            return (
              <div
                key={apt.id}
                className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-6 hover:border-[#00e5c7]/30 transition-colors"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-[#0a1628] rounded-lg flex flex-col items-center justify-center">
                  <span
                    className="text-xl font-bold text-white leading-none"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {d.day}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#00e5c7] font-semibold">
                    {d.month}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-[#0a1628]">
                    {apt.title}
                  </h4>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    <span className="flex items-center gap-1 text-xs text-[#5a6a7e]">
                      <i className="ri-time-line text-xs"></i>
                      {apt.time}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#5a6a7e]">
                      <i className="ri-user-line text-xs"></i>
                      {apt.advisor}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#5a6a7e]">
                      {apt.type === 'Video Call' ? (
                        <i className="ri-video-chat-line text-xs"></i>
                      ) : (
                        <i className="ri-map-pin-line text-xs"></i>
                      )}
                      {apt.type}
                    </span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/15 text-emerald-500 border border-emerald-500/20 whitespace-nowrap">
                  {apt.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Past Appointments */}
      <div>
        <h3 className="text-sm uppercase tracking-wider text-[#5a6a7e] font-semibold mb-4">
          Past Appointments
        </h3>
        <div className="space-y-3">
          {pastAppointments.map((apt) => {
            const d = formatDate(apt.date);
            return (
              <div
                key={apt.id}
                className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-6 opacity-70"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
                  <span
                    className="text-xl font-bold text-[#5a6a7e] leading-none"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {d.day}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#5a6a7e] font-semibold">
                    {d.month}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-[#0a1628]">
                    {apt.title}
                  </h4>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    <span className="flex items-center gap-1 text-xs text-[#5a6a7e]">
                      <i className="ri-time-line text-xs"></i>
                      {apt.time}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#5a6a7e]">
                      <i className="ri-user-line text-xs"></i>
                      {apt.advisor}
                    </span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-gray-100 text-[#5a6a7e] whitespace-nowrap">
                  {apt.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
